/**
 * Einheiten einer Leistungsposition — bewusst ein blattloses Modul.
 *
 * WARUM EIGENES MODUL: isHourlyUnit lag in projectLv.ts, und projectLv liest
 * ueber altCoveredQuantities aus externalAdvances. Als externalAdvances die
 * Erkennung fuer seinen Einheiten-Riegel brauchte, entstand ein Importzyklus
 * externalAdvances → projectLv → externalAdvances. Heute ist er harmlos (beide
 * Bindungen werden erst im Funktionsrumpf gelesen), aber projectLv haengt unter
 * invoiceLogic/invoiceService: eine spaetere Verwendung auf Modulebene waere
 * ein TDZ-Absturz im Rechnungslauf. Dieses Modul importiert nichts und kann
 * deshalb von beiden Seiten gelesen werden.
 */

/**
 * Rechnet die Position in STUNDEN ab statt in Stueck?
 *
 * Der Unterschied ist tragend, sobald irgendwo Begehungen GEZAEHLT werden. Eine
 * Position "Sicherheitsbegehung nach Zeitaufwand" (2.7.40, Einheit Stunden)
 * traegt in quantityUsed einen Stundenstand — 22 Begehungen koennen dort 28
 * Stunden ergeben. Wer sie wie eine Stueckposition behandelt, verwechselt beide
 * Zahlen: der Nachtragsdialog ueberschrieb den Stundenstand mit dem
 * Begehungszaehler, und der Projektcheck verglich Stunden mit der Zahl
 * archivierter Berichte.
 *
 * Bewusst GROSSZUEGIGER als ein Vergleich auf den exakten String 'Stunden':
 * der Angebotseditor erzeugt 'Std', importierte Positionen tragen 'h'. Eine
 * Position zu viel als Stundenposition zu erkennen kostet einen nicht
 * nachgezogenen Zaehler, eine zu wenig kostet einen ueberschriebenen
 * Leistungsstand — oder, in den Buchungspfaden, eine Buchung von 1 statt der
 * tatsaechlichen Stunden.
 *
 * Die einzige MENGENWIRKSAME Stundenerkennung der Anwendung: die Buchungspfade
 * (reportBooking, SiGeKoInspectionModule, UfsA-Ansichten, SiGePlanEditor), die
 * Eingabedialoge, der Projektcheck, der Einheiten-Riegel der Alt-Abschlaege
 * (externalAdvances.unitMismatch) und der Leistungsnachweis verwenden sie,
 * damit dieselbe Position ueberall dieselbe Bedeutung hat. Daneben gibt es nur
 * noch resolveUnitCode (einvoice/ublInvoice), das aus derselben Einheit den
 * UN/ECE-Code HUR ableitet — eine Formatfrage, keine Mengenfrage.
 */
export const isHourlyUnit = (unit?: string | null): boolean => {
    const u = (unit || '').trim().toLowerCase();
    return u === 'h' || u === 'std' || u === 'std.' || u.includes('stunde');
};

/**
 * Rechnet die Position in MONATEN ab (Monatspauschale)?
 *
 * Bewusst GROSSZUEGIG wie isHourlyUnit, und aus demselben Grund: die Einheit
 * ist freier Text aus Angebotseditor und LV-Import. 'Monat', 'Monate', 'mon',
 * 'mtl.' und 'monatlich' meinen alle dasselbe; wer hier auf den exakten
 * String prueft, verliert die Erkennung bei der ersten importierten Position
 * — und damit den Monatsvorschlag und den FLAT-Riegel der Pauschale
 * (services/flatRateLogic.ts). Eine Position zu viel als Monatsposition zu
 * erkennen ist harmlos, solange sie keine flatRate-Deckung traegt.
 */
export const isMonthlyUnit = (unit?: string | null): boolean => {
    const u = (unit || '').trim().toLowerCase();
    return u === 'mon' || u === 'mon.' || u === 'mtl' || u === 'mtl.' || u.includes('monat');
};

/** Einheit ohne Gross-/Kleinschreibung, Leerraum und abschliessenden Punkt. */
const unitKey = (unit?: string | null): string =>
    (unit || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/\.$/, '');

const DAILY_UNITS = new Set([
    'tag', 'tage', 'at', 'tw', 'pt', 'd',
    'arbeitstag', 'arbeitstage', 'einsatztag', 'einsatztage', 'personentag', 'personentage',
    'tagessatz', 'tagessätze', 'tagessaetze', 'tagespauschale', 'tagespauschalen',
    'tagewerk', 'tagwerk', 'tageswerk',
]);

/**
 * Rechnet die Position in (ganzen) TAGEN ab — Tagessatz, Arbeitstag, Tagewerk?
 *
 * Gebraucht von der Auslastung des KPI-Cockpits: ein gebuchter Tag ist ein
 * Arbeitstag von acht Stunden, nicht „eine Begehung“ nach Preisstaffel. Ein
 * HALBER Tag ist ausdruecklich KEIN Tag (`Halbtag` enthaelt `tag`) — dafuer
 * gilt die Titelregel der Auslastung (4 Stunden).
 */
export const isDailyUnit = (unit?: string | null): boolean => {
    const u = unitKey(unit);
    if (!u || u.includes('halb')) return false;
    return DAILY_UNITS.has(u);
};

const WEEKLY_UNITS = new Set([
    'woche', 'wochen', 'wo', 'kw', 'arbeitswoche', 'arbeitswochen',
    'wochenpauschale', 'wochenpauschalen', 'wochensatz', 'wochensätze', 'wochensaetze',
]);

/** Rechnet die Position in WOCHEN ab (fuenf Arbeitstage)? */
export const isWeeklyUnit = (unit?: string | null): boolean => WEEKLY_UNITS.has(unitKey(unit));

/** The little a booking dialog needs to propose an hours value. */
export interface HoursDefaultLike {
    unit?: string | null;
    minHours?: number;
}

/**
 * Was der Rohtext eines Mindeststunden-Feldes ueber den MODELLWERT aussagt.
 *
 * Drei Faelle, und der dritte ist der, an dem eine einfache "parse or
 * undefined"-Regel scheitert:
 *  - `clear`   — das Feld ist leer: die Vorgabe wird bewusst zurueckgenommen.
 *  - `value`   — eine brauchbare Zahl > 0.
 *  - `pending` — eine Zwischenstufe beim Tippen ("0", "0.", "-", "abc").
 *
 * `pending` darf das Modell NICHT anfassen. Wer bei einer Position mit
 * vereinbarten 2 Std. "0.25" eintippt, steht nach dem ersten Zeichen bei "0";
 * wuerde das als "keine Vorgabe" durchgereicht, loeschte eine halb getippte
 * Eingabe die vereinbarte Zahl — inklusive Protokolleintrag und NULL in der
 * Datenbank. Nur ein LEERES Feld nimmt eine Vorgabe zurueck.
 */
export type MinHoursInput =
    | { kind: 'clear' }
    | { kind: 'value'; value: number }
    | { kind: 'pending' };

/**
 * Die Form, die eine Eingabe VOLLSTAENDIG haben muss, damit sie ueberhaupt
 * geparst wird: Vorzeichen, Ziffern, HOECHSTENS ein Trennzeichen (Punkt oder
 * Komma), Nachkommastellen — sonst nichts.
 *
 * WARUM eine Form-Pruefung statt parseFloat allein: parseFloat nimmt ein
 * gueltiges PRAEFIX und verwirft den Rest stillschweigend. Aus "3..5" wird 3,
 * aus "1,,5" wird 1. Wer 3,5 Std. meinte und sich vertippt, sah bis dahin kein
 * rotes Feld, keine Sperre — nur eine halbe Stunde weniger auf der Rechnung.
 * Das Zahlenfeld von frueher (type="number") fing das noch ueber badInput ab;
 * seit die Felder ihren Rohtext selbst halten (type="text" + inputMode,
 * zwingend — ein Zahlenfeld zerstoert "2."), muss die Regel es tun.
 *
 * Der Trenner darf am ENDE stehen ("2." / "2,"): das ist die Zwischenstufe auf
 * dem Weg zu "2.75" und wird als sichtbare 2 gelesen, nie als Verlust.
 */
const DECIMAL_SHAPE = /^-?(\d+([.,]\d*)?|[.,]\d+)$/;

/**
 * Die EINE Regel, wie ein Mindeststunden-Feld gelesen wird. Alle drei
 * Eingabestellen (Wizard, Angebotsannahme, Nachtragsdialog) haengen daran —
 * und seit dem Abrechnungsdialog auch dessen Mengenfelder.
 */
export const readMinHoursInput = (raw: string): MinHoursInput => {
    const text = String(raw ?? '').trim();
    if (text === '') return { kind: 'clear' };
    if (!DECIMAL_SHAPE.test(text)) return { kind: 'pending' };
    // Komma wie Punkt lesen: die deutsche Tastatur liefert "0,25". Die Form
    // oben laesst hoechstens ein Trennzeichen zu, ein einzelnes replace reicht.
    const value = parseFloat(text.replace(',', '.'));
    return Number.isFinite(value) && value > 0 ? { kind: 'value', value } : { kind: 'pending' };
};

/**
 * Der neue Modellwert nach einer Eingabe — die Anwendung der Regel oben, damit
 * die Fallunterscheidung nicht in drei Komponenten steht.
 *
 * WICHTIG fuer die Aufrufer: das Feld muss seinen ROHTEXT selbst halten und
 * darf nicht aus dem Modellwert zurueckgespeist werden. Sonst verschwindet jede
 * Zwischenstufe unter dem Cursor: "0" waere kein Modellwert, das Feld leerte
 * sich, und die naechsten Zeichen ergaeben 25 statt 0.25.
 */
export const applyMinHoursInput = (raw: string, current?: number): number | undefined => {
    const result = readMinHoursInput(raw);
    if (result.kind === 'clear') return undefined;
    if (result.kind === 'value') return result.value;
    return current;
};

/**
 * Hours to PRE-FILL an entry field with for this position.
 *
 * Single source of the default so no dialog carries its own hard-coded 1: the
 * booking dialogs used to start at 1.0 regardless of what was agreed, and a
 * project with a two-hour minimum lost half an hour on every booking nobody
 * corrected by hand. Returns the project's minHours for hourly positions and
 * falls back to 1 everywhere else — for a non-hourly unit, for a missing
 * minHours (a position whose minimum was never agreed) and for values that are
 * not a usable number (0, negative, NaN, Infinity).
 *
 * The value is a PROPOSAL, never a floor: the field stays freely editable and
 * a lower entry only triggers a warning.
 */
export const defaultHoursForService = (service: HoursDefaultLike): number =>
    agreedMinHours(service) ?? 1;

/**
 * Die AUSDRUECKLICH vereinbarten Mindeststunden dieser Position — sonst null.
 *
 * Die eine Stelle, die "es wurde etwas vereinbart" von "es wurde nichts
 * vereinbart" trennt. defaultHoursForService kann das nicht: es faellt auf 1
 * zurueck, und eine vereinbarte 1 ist von einer fehlenden Vereinbarung dann
 * nicht mehr zu unterscheiden. Genau diese Unterscheidung brauchen die
 * Dialoge — der Hinweis "Mindestens X Std. ansetzen." darf nur erscheinen, wo
 * ein X vereinbart wurde, und ein Feld, das ohne Vereinbarung bewusst leer
 * startet, darf nicht stillschweigend eine 1 erben.
 *
 * null fuer eine Stueckposition (Stunden sind dort bedeutungslos), fuer eine
 * fehlende Angabe und fuer alles, was keine brauchbare Zahl ist (0, negativ,
 * NaN, Infinity).
 */
export const agreedMinHours = (service: HoursDefaultLike): number | null => {
    if (!isHourlyUnit(service?.unit)) return null;
    const min = service?.minHours;
    return typeof min === 'number' && Number.isFinite(min) && min > 0 ? min : null;
};

/**
 * Zahl -> Rohtext eines Eingabefeldes in deutscher Schreibweise (1.5 -> "1,5",
 * 2 -> "2"), wie ihn der Hinweistext daneben zeigt.
 *
 * OHNE Tausendertrenner, und das ist keine Kosmetik: "1.234,5" traegt ZWEI
 * Trennzeichen, und DECIMAL_SHAPE laesst hoechstens eines zu — eine so
 * vorbelegte Eingabe waere fuer die eigene Leseregel `pending`, also ein Feld,
 * das gefuellt aussieht und nichts buchen wuerde.
 */
const formatHoursInputText = (value: number): string =>
    value.toLocaleString('de-DE', { useGrouping: false });

/**
 * ROHTEXT, mit dem ein manuelles Buchungsfeld startet (ServiceTrackingModal).
 *
 * Drei Faelle, und der mittlere ist der, um den es hier geht:
 *  - Stundenposition MIT vereinbarter Vorgabe -> die Vorgabe. Bisher stand
 *    dort eine 0, und wer die vereinbarten 2 Std. nicht von Hand nachtrug,
 *    buchte zu wenig. Die Zahl ist ein VORSCHLAG, keine Untergrenze: das Feld
 *    bleibt frei, ein Unterschreiten warnt nur.
 *  - Stundenposition OHNE Vereinbarung -> leer, also unveraendert wie bisher.
 *    Der Dialog startet Stunden bewusst bei 0, damit die Dauer bewusst erfasst
 *    wird; ohne vereinbarte Zahl gaebe es nur zu raten, und eine Position ohne
 *    Vereinbarung darf keine geschenkt bekommen.
 *  - Stueckposition -> "1", ebenfalls unveraendert.
 *
 * Rohtext und nicht Zahl, weil das Feld type="text" ist und seinen Text selbst
 * haelt (siehe readMinHoursInput): ein Zahlenfeld zerstoert "2.".
 *
 * DEUTSCHE Schreibweise, weil direkt unter dem Feld der Hinweis "Mindestens 1,5
 * Std. ansetzen." steht: eine Vorbelegung "1.5" haette denselben Wert in zwei
 * Schreibweisen im selben Dialog gezeigt. Die Regel oben liest beide, die
 * Anzeige soll trotzdem nur eine kennen.
 */
export const initialBookingAmountText = (service: HoursDefaultLike): string => {
    if (!isHourlyUnit(service?.unit)) return '1';
    const min = agreedMinHours(service);
    return min === null ? '' : formatHoursInputText(min);
};

/**
 * Die MENGEN hinter den Rohtexten einer Eingabemaske — nur, was wirklich
 * gebucht werden darf.
 *
 * Fuer Masken, in denen ein LEERES Feld "nicht erbracht" bedeutet
 * (AdditionalServicesModal): jede Position haelt ihren Rohtext, und nur eine
 * positive Zahl wird zu einem Eintrag. Leer, "0" und alles Unlesbare ("-",
 * "abc", "3..5") tragen KEINEN Eintrag bei — sie duerfen weder eine 0 buchen
 * noch den getippten Text anfassen; der bleibt im Feld stehen und wird zum
 * Eintrag, sobald er lesbar ist. Der haengende Trenner ("2." / "2,") zaehlt wie
 * ueberall als die sichtbare 2, nicht als Verlust der Position.
 *
 * Bewusst dieselbe Leseregel wie die Mindeststunden-Felder (readMinHoursInput):
 * ein zweiter Parser hiesse, dass "3..5" an einer Stelle 3 ergibt und an der
 * anderen nichts.
 */
export const bookableQuantities = (texts: Record<string, string>): Record<string, number> => {
    const quantities: Record<string, number> = {};
    Object.entries(texts || {}).forEach(([id, raw]) => {
        const parsed = readMinHoursInput(raw);
        if (parsed.kind === 'value') quantities[id] = parsed.value;
    });
    return quantities;
};
